"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative font-default text-foreground text-[12px] uppercase tracking-[0.18em] px-0 py-2 bg-transparent hover:bg-transparent focus:bg-transparent transition-colors duration-200 group">
                <span>{item.label}</span>
                <span className="absolute left-0 -bottom-0.5 h-px w-0 bg-foreground transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 px-6 pt-4">
        <div className="h-5 w-32 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-32 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-32 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-32 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-1 px-6 pt-2">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onNavigate} className="font-default text-foreground text-sm uppercase tracking-[0.18em] py-4 border-b border-border/60 hover:text-primary transition-colors duration-200">
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="3570" id="header" className={`sticky top-0 z-50 w-full transition-all duration-200 bg-background text-foreground ${scrolled ? "bg-background/80 backdrop-blur-md border-b border-border" : "bg-background border-b border-transparent"}`}>
      <div className="mx-auto flex items-center justify-between px-6 md:px-12 py-7">
        {}
        <Link to="/HomePage" aria-label="Joanne Glow Studio home" className="flex items-center">
          <WvcLogo className="h-7 w-auto" />
        </Link>

        {}
        <div className="hidden lg:flex items-center justify-center flex-1">
          <MenuProvider menu_id="2" className="flex items-center">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden lg:flex items-center">
          <Button asChild className="px-6 py-3 h-auto bg-primary text-primary-foreground hover:bg-primary/90 font-default text-[12px] uppercase tracking-[0.18em]">
            <Link to="/GetInTouchPage">Reserve Your Date</Link>
          </Button>
        </div>

        {}
        <div className="flex lg:hidden items-center gap-3">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu" className="text-foreground hover:bg-accent">
                <Menu className="size-5" strokeWidth={1.25} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-background w-full sm:max-w-sm p-0 flex flex-col">
              <SheetHeader className="px-6 pt-6 pb-2">
                <SheetTitle className="font-default text-2xl font-normal text-foreground tracking-tight">
                  JOANNE GLOW
                </SheetTitle>
              </SheetHeader>

              <MenuProvider menu_id="2" className="flex-1">
                <MobileMenuConsumer onNavigate={() => setMobileOpen(false)} />
              </MenuProvider>

              <div className="px-6 pb-8 pt-6 mt-auto">
                <Button asChild className="w-full px-6 py-3 h-auto bg-primary text-primary-foreground hover:bg-primary/90 font-default text-[12px] uppercase tracking-[0.18em]">
                  <Link to="/GetInTouchPage" onClick={() => setMobileOpen(false)}>
                    Reserve Your Date
                  </Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}